import React, { useState, useEffect } from 'react';
import { BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnalysisConfigCard } from '@/components/market-analysis/cards/AnalysisConfigCard';
import { LivePriceFeedsCard } from '@/components/market-analysis/cards/LivePriceFeedsCard';
import { TrendsAnalysisCard } from '@/components/market-analysis/cards/TrendsAnalysisCard';
import { KeyLevelsCard } from '@/components/market-analysis/cards/KeyLevelsCard';
import { PriceTargetsCard } from '@/components/market-analysis/cards/PriceTargetsCard';
import { EnhancedAiRecommendationCard } from '@/components/market-analysis/cards/EnhancedAiRecommendationCard';
import { AnalysisSummaryCard } from '@/components/market-analysis/cards/AnalysisSummaryCard';
import { AiAnalysisProgress } from '@/components/market-analysis/AiAnalysisProgress';
import { AffiliateAccessDenied } from '@/components/market-analysis/AffiliateAccessDenied';
import { UsageLimitWarningDialog } from '@/components/market-analysis/UsageLimitWarningDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAiMarketAnalysis } from '@/hooks/useAiMarketAnalysis';
import { useAffiliateMarketAccess } from '@/hooks/useAffiliateMarketAccess';
import { useMarketAnalysisUsage } from '@/hooks/useMarketAnalysisUsage';
import { useAuth } from '@/contexts/AuthContext';
import { MarketSummaryService } from '@/services/MarketSummaryService';
import { SymbolService, SymbolData } from '@/services/SymbolService';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import '@/components/dashboard/dashboard-animations.css';

const MarketAnalysisPage = () => {
  const { t, ready } = useTranslation();
  const { user } = useAuth();
  const { hasMarketAccess, isLoading: isLoadingAccess } = useAffiliateMarketAccess();
  const { 
    canUse, 
    useMarketAnalysis, 
    remainingDaily,
    remainingMonthly,
    dailyLimit,
    monthlyLimit,
    source,
    isLoading: isLoadingUsage,
    isIncrementing
  } = useMarketAnalysisUsage();
  
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('vi'); // Default to Vietnamese
  const [selectedTimeframe, setSelectedTimeframe] = useState('');
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [isCreatingMarketSummary, setIsCreatingMarketSummary] = useState(false);
  const [symbols, setSymbols] = useState<SymbolData[]>([]);
  const [isLoadingSymbols, setIsLoadingSymbols] = useState(true);
  const [symbolsError, setSymbolsError] = useState<string>();
  const [showUsageWarning, setShowUsageWarning] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // AI Analysis Integration
  const { 
    startAnalysis: startAiAnalysis, 
    analysisSession, 
    isAnalyzing: isAiAnalyzing, 
    error: aiError,
    stopPolling 
  } = useAiMarketAnalysis({ userId: user?.id || null });

  const timeframes = [
    { value: 'short', label: 'Ngắn hạn (1D - 1W)' },
    { value: 'medium', label: 'Trung hạn (1W - 1M)' },
    { value: 'long', label: 'Dài hạn (1M - 3M)' },
  ];

  // Load available symbols from database
  useEffect(() => {
    const loadSymbols = async () => {
      setIsLoadingSymbols(true);
      setSymbolsError(undefined);
      
      try {
        const result = await SymbolService.getAvailableSymbols();
        
        if (result.success && result.data) {
          setSymbols(result.data);
          console.log('Loaded symbols:', result.data);
        } else {
          setSymbolsError(result.error || 'Failed to load symbols');
          toast.error('Failed to load available symbols');
        }
      } catch (error) {
        console.error('Error loading symbols:', error);
        setSymbolsError('Failed to load symbols');
        toast.error('Failed to load available symbols');
      } finally {
        setIsLoadingSymbols(false);
      }
    };

    loadSymbols();
  }, []);

  const handleStartAnalysis = async () => {
    if (!selectedSymbol || !user?.id) {
      console.log('❌ [PAGE] Missing required data:', { selectedSymbol, userId: user?.id });
      return;
    }
    
    console.log('🚀 [PAGE] Starting analysis process...');
    console.log('🚀 [PAGE] User can use:', canUse);
    
    // Clear previous errors
    setAnalysisError(null);
    
    // Step 1: Check and increment usage FIRST
    console.log('📈 [PAGE] Step 1: Checking and incrementing usage...');
    const usageSuccess = await useMarketAnalysis();
    
    if (!usageSuccess) {
      console.log('❌ [PAGE] Usage increment failed, stopping analysis');
      return; // useMarketAnalysis already shows error messages
    }
    
    console.log('✅ [PAGE] Step 1 completed - Usage incremented successfully');
    
    // Step 2: Show success message for usage
    toast.success('Analysis quota used successfully', {
      description: `Remaining today: ${remainingDaily - 1}/${dailyLimit}`
    });
    
    // Step 3: Prepare market data
    console.log('📊 [PAGE] Step 2: Preparing market data...');
    setIsCreatingMarketSummary(true);
    
    try {
      // Check for existing market summary
      const existingSummaries = await MarketSummaryService.getMarketSummaries(1, selectedSymbol);
      let marketSummaryId: string;

      if (existingSummaries.length > 0) {
        marketSummaryId = existingSummaries[0].id;
        console.log('📊 [PAGE] Using existing market summary:', marketSummaryId);
        toast.success('Found existing market data for analysis');
      } else {
        console.log('📊 [PAGE] Creating new market summary...');
        
        const result = await MarketSummaryService.createMarketSummary({
          symbol: selectedSymbol,
          timeframe: selectedTimeframe || 'medium',
          signals_analysis: {
            current_price: 0,
            trend_analysis: {
              short_term: 'neutral',
              medium_term: 'neutral', 
              long_term: 'neutral'
            },
            volume_analysis: {
              volume_trend: 'stable',
              relative_volume: 1.0
            },
            momentum: {
              rsi: 50,
              macd: 'neutral'
            }
          },
          sentiment_data: {
            overall_sentiment: 'neutral',
            news_sentiment: 0.5,
            social_sentiment: 0.5
          },
          support_levels: [],
          resistance_levels: [],
          market_condition: 'NORMAL'
        });

        if (!result.success || !result.data) {
          throw new Error(result.error || 'Failed to create market summary');
        }

        marketSummaryId = result.data.id;
        console.log('📊 [PAGE] Created new market summary:', marketSummaryId);
        toast.success('Market data prepared for analysis');
      }

      // Step 4: Start AI Analysis (independent of usage)
      console.log('🤖 [PAGE] Step 3: Starting AI analysis...');
      setShowAiAnalysis(true);
      
      try {
        await startAiAnalysis(selectedSymbol, marketSummaryId, selectedLanguage);
        console.log('✅ [PAGE] AI analysis started successfully');
      } catch (aiAnalysisError) {
        console.error('❌ [PAGE] AI analysis failed:', aiAnalysisError);
        setAnalysisError(aiAnalysisError instanceof Error ? aiAnalysisError.message : 'AI analysis failed');
        
        // Show error but keep analysis section visible since usage was already consumed
        toast.error('AI analysis failed to start', {
          description: 'Your quota has been used. You can retry without additional quota usage.'
        });
      }
      
    } catch (error) {
      console.error('❌ [PAGE] Market data preparation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to prepare market data';
      setAnalysisError(errorMessage);
      setShowAiAnalysis(true); // Show error section
      toast.error('Failed to prepare market data', {
        description: errorMessage
      });
    } finally {
      setIsCreatingMarketSummary(false);
    }
  };

  const handleStopAnalysis = () => {
    stopPolling();
    setShowAiAnalysis(false);
    setAnalysisError(null);
  };

  const handleRetryAnalysis = async () => {
    if (!selectedSymbol || !user?.id) return;
    
    setAnalysisError(null);
    
    try {
      const existingSummaries = await MarketSummaryService.getMarketSummaries(1, selectedSymbol);
      if (existingSummaries.length > 0) {
        const marketSummaryId = existingSummaries[0].id;
        console.log('🔄 Retrying AI analysis for symbol:', selectedSymbol);
        await startAiAnalysis(selectedSymbol, marketSummaryId, selectedLanguage);
      } else {
        throw new Error('No market summary found for retry');
      }
    } catch (error) {
      console.error('❌ Retry failed:', error);
      setAnalysisError(error instanceof Error ? error.message : 'Retry failed');
      toast.error('Retry failed', {
        description: 'Please contact support if the issue persists'
      });
    }
  };

  const handleUpgrade = () => {
    toast.info('Upgrade functionality will be implemented soon');
  };

  const handleContactAffiliate = () => {
    toast.info('Contact affiliate functionality will be implemented soon');
  };

  // Helper function to check if a step result is available
  const isStepResultAvailable = (stepKey: string) => {
    return analysisSession?.card_results?.[stepKey]?.status === 'completed';
  };

  // Helper function to get step result data
  const getStepResultData = (stepKey: string) => {
    return analysisSession?.card_results?.[stepKey]?.data;
  };

  // Check if analysis is actively running (not completed)
  const isAnalysisRunning = analysisSession && 
    (analysisSession.status === 'PROCESSING' || analysisSession.status === 'IN_PROGRESS');

  // Show loading state while checking access or i18n is not ready
  if (isLoadingAccess || isLoadingUsage || !ready) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm sm:text-base">Đang kiểm tra quyền truy cập...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have affiliate access
  if (!hasMarketAccess && !canUse) {
    return <AffiliateAccessDenied />;
  }

  return (
    <div className="bg-gradient-to-br from-background via-background to-muted/20 relative min-h-full">
      {/* Enhanced animated background particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-emerald-500/20 rounded-full animate-pulse floating-action" />
        <div className="absolute top-40 right-20 w-1 h-1 bg-blue-500/30 rounded-full animate-pulse floating-action animate-delay-100" />
        <div className="absolute bottom-40 left-20 w-1.5 h-1.5 bg-purple-500/20 rounded-full animate-pulse floating-action animate-delay-200" />
        <div className="absolute bottom-20 right-10 w-1 h-1 bg-amber-500/30 rounded-full animate-pulse floating-action animate-delay-300" />
        <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-rose-500/20 rounded-full animate-pulse floating-action animate-delay-400" />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-cyan-500/25 rounded-full animate-pulse floating-action animate-delay-500" />
      </div>

      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8 relative z-10">
        {/* Enhanced Header - Mobile Optimized */}
        <div className="text-center mb-6 sm:mb-10 animate-fade-in">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent border border-emerald-500/20 backdrop-blur-sm card-shine floating-action soft-glow">
              <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500 icon-spin-hover" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-foreground via-foreground to-emerald-600 bg-clip-text text-transparent hover-scale">
              Market Analysis AI
            </h1>
          </div>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto gradient-animate bg-gradient-to-r from-muted-foreground to-muted-foreground/80 bg-clip-text px-4">
            Advanced market analysis powered by artificial intelligence with professional-grade insights and predictions
          </p>
        </div>

        {/* Analysis Configuration Card - Mobile Optimized */}
        <div className="animate-fade-in animate-delay-100">
          <AnalysisConfigCard
            selectedSymbol={selectedSymbol}
            setSelectedSymbol={setSelectedSymbol}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            isAnalyzing={isAiAnalyzing || isCreatingMarketSummary || isIncrementing}
            onAnalysis={handleStartAnalysis}
            symbols={symbols}
            isLoadingSymbols={isLoadingSymbols}
            symbolsError={symbolsError}
          />
          
          {/* Enhanced AI Analysis Controls with Better Error Handling */}
          {selectedSymbol && (
            <div className="mt-4 p-3 sm:p-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-xl border border-purple-500/20">
              <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
                <div className={isMobile ? 'text-center' : ''}>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">{t('market_analysis.ai_powered_analysis')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{t('market_analysis.ai_analysis_description')}</p>
                  {!canUse && (
                    <p className="text-xs sm:text-sm text-amber-600 mt-1">
                      ⚠️ You have reached your daily limit. {source === 'affiliate' ? 'Contact your affiliate for more quota.' : 'Upgrade to a paid plan for unlimited access.'}
                    </p>
                  )}
                  {canUse && remainingDaily <= 2 && (
                    <p className="text-xs sm:text-sm text-amber-600 mt-1">
                      ⚠️ You have {remainingDaily} analyses remaining today.
                    </p>
                  )}
                  {analysisError && (
                    <p className="text-xs sm:text-sm text-red-600 mt-1">
                      ❌ {analysisError}
                    </p>
                  )}
                </div>
                <div className={`flex flex-col gap-2 ${isMobile ? 'items-center' : 'items-end'}`}>
                  {!showAiAnalysis ? (
                    <Button 
                      onClick={handleStartAnalysis}
                      disabled={!canUse || isAiAnalyzing || isCreatingMarketSummary || isIncrementing}
                      className={`bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 ${isMobile ? 'w-full sm:w-auto' : ''}`}
                      size={isMobile ? "lg" : "default"}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {isIncrementing ? 'Recording Usage...' : isCreatingMarketSummary ? 'Preparing Analysis...' : 'Start Analysis'}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleStopAnalysis}
                        variant="outline"
                        className={`border-purple-500/30 text-purple-600 hover:bg-purple-500/10 ${isMobile ? 'w-full sm:w-auto' : ''}`}
                        size={isMobile ? "lg" : "default"}
                      >
                        Stop Analysis
                      </Button>
                      {analysisError && (
                        <Button 
                          onClick={handleRetryAnalysis}
                          className={`bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 ${isMobile ? 'w-full sm:w-auto' : ''}`}
                          size={isMobile ? "lg" : "default"}
                        >
                          Retry Analysis
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Live Price Feeds - Show always when symbol is selected */}
        {selectedSymbol && (
          <div className="animate-fade-in animate-delay-150">
            <LivePriceFeedsCard symbol={selectedSymbol} />
          </div>
        )}

        {/* Error Display */}
        {showAiAnalysis && analysisError && (
          <div className="animate-fade-in animate-delay-200">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <h3 className="font-semibold text-red-700">AI Analysis Error</h3>
              </div>
              <p className="text-sm text-red-600 mb-3">{analysisError}</p>
              <div className="text-xs text-muted-foreground">
                <p>• Your usage quota has been consumed</p>
                <p>• You can retry the analysis without additional quota usage</p>
                <p>• Contact support if the issue persists</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis Progress - Only show when analysis is running */}
        {isAnalysisRunning && (
          <div className="animate-fade-in animate-delay-200">
            <AiAnalysisProgress 
              session={analysisSession}
              onCancel={handleStopAnalysis}
            />
          </div>
        )}

        {/* Show partial results as they become available during processing */}
        {analysisSession && (analysisSession.status === 'PROCESSING' || analysisSession.status === 'IN_PROGRESS' || analysisSession.status === 'COMPLETED') && (
          <div className="space-y-4 sm:space-y-6">
            {/* Key Levels and Price Targets - Mobile Responsive Layout */}
            <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
              {/* Key Levels - Show if available */}
              {isStepResultAvailable('key_levels') && (
                <div className="animate-fade-in animate-delay-300">
                  <KeyLevelsCard supportResistance={getStepResultData('key_levels')} />
                </div>
              )}

              {/* Price Targets - Show if available */}
              {isStepResultAvailable('price_targets') && (
                <div className="animate-fade-in animate-delay-350">
                  <PriceTargetsCard 
                    priceTargets={getStepResultData('price_targets')}
                    confidence={getStepResultData('ai_recommendation')?.confidence || 0}
                  />
                </div>
              )}
            </div>

            {/* Trends Analysis - Full Width Card */}
            {isStepResultAvailable('trends_analysis') && (
              <div className="animate-fade-in animate-delay-400">
                <TrendsAnalysisCard trends={getStepResultData('trends_analysis')} />
              </div>
            )}

            {/* AI Recommendation - Show if available */}
            {isStepResultAvailable('ai_recommendation') && (
              <div className="animate-fade-in animate-delay-500">
                <EnhancedAiRecommendationCard 
                  data={getStepResultData('ai_recommendation')}
                />
              </div>
            )}

            {/* Analysis Summary - Show if available */}
            {isStepResultAvailable('analysis_summary') && (
              <div className="animate-fade-in animate-delay-600">
                <AnalysisSummaryCard 
                  riskLevel={getStepResultData('analysis_summary').riskLevel}
                  analysis={getStepResultData('analysis_summary').summary}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage Limit Warning Dialog */}
      <UsageLimitWarningDialog
        open={showUsageWarning}
        onOpenChange={setShowUsageWarning}
        source={source}
        remainingDaily={remainingDaily}
        remainingMonthly={remainingMonthly}
        dailyLimit={dailyLimit}
        monthlyLimit={monthlyLimit}
        onUpgrade={handleUpgrade}
        onContactAffiliate={handleContactAffiliate}
      />
    </div>
  );
};

export default MarketAnalysisPage;
